import {
  EBlockType,
  IOnePlusManyStoreBlock,
  OfferData,
  SequencedProduct,
  StoreBlockMaxOffers,
  TStoreBlock,
} from '../../constants/apiResponses.types';
import { getCurrentSubRollingOffer } from '../../utils';
import { BLOCK_TYPE_TO_BODY_BLOCK } from './storeBlocks.utils';

const BUY_ONE_GET_MANY_EVENT_TYPE = 'one_plus_many';

const isOnePlusManyStoreBlock = (
  block: TStoreBlock,
): block is IOnePlusManyStoreBlock => block.type === EBlockType.ONE_PLUS_MANY;

const BLOCK_TO_OFFERS_KEY: Record<string, keyof IBuyOneGetManyOffersByBlock> = {
  onePlusMany: 'onePlusMany',
  progressBars: 'progressBars',
  specialOffers: 'promotions',
  rollingOffers: 'rolling',
  bundles: 'bundles',
};

const deriveStoreBlockTypeToOffersKey = (): Partial<
  Record<EBlockType, keyof IBuyOneGetManyOffersByBlock>
> => {
  const blockTypeToOffersKey: Partial<
    Record<EBlockType, keyof IBuyOneGetManyOffersByBlock>
  > = {};
  (Object.keys(BLOCK_TYPE_TO_BODY_BLOCK) as EBlockType[]).forEach((blockType) => {
    const offersKey = BLOCK_TO_OFFERS_KEY[BLOCK_TYPE_TO_BODY_BLOCK[blockType]];
    if (offersKey !== undefined) {
      blockTypeToOffersKey[blockType] = offersKey;
    }
  });
  return blockTypeToOffersKey;
};

const STORE_BLOCK_TYPE_TO_OFFERS_KEY = deriveStoreBlockTypeToOffersKey();

export interface IBuyOneGetManyOffersByBlock {
  onePlusMany?: OfferData[];
  progressBars?: OfferData[];
  promotions?: OfferData[];
  rolling?: OfferData[];
  bundles?: OfferData[];
}

export interface IBuyOneGetManyEventSharedContext {
  os?: string;
  browser?: string;
  countryCode?: string;
}

const getOnePlusManyOffers = (
  offers: IBuyOneGetManyOffersByBlock,
): OfferData[] => {
  const onePlusManyOffers = offers.onePlusMany;
  if (onePlusManyOffers === undefined) return [];
  return onePlusManyOffers;
};

const findOfferIndex = (offerList: OfferData[], offerId: string): number =>
  offerList.findIndex((offer) => offer.offerId === offerId);

const countOffersForKey = (
  offers: IBuyOneGetManyOffersByBlock,
  offersKey: keyof IBuyOneGetManyOffersByBlock,
): number => {
  const offersForKey = offers[offersKey];
  if (offersForKey === undefined) return 0;
  return offersForKey.length;
};

const countPrecedingOffers = (
  order: string[],
  onePlusManyBlockPosition: number,
  offers: IBuyOneGetManyOffersByBlock,
): number => {
  let precedingOffersCount = 0;
  for (let blockIndex = 0; blockIndex < onePlusManyBlockPosition; blockIndex++) {
    const offersKey = BLOCK_TO_OFFERS_KEY[order[blockIndex]];
    if (offersKey === undefined || offersKey === 'onePlusMany') continue;
    precedingOffersCount += countOffersForKey(offers, offersKey);
  }
  return precedingOffersCount;
};

const computeOfferPositionFromBodyBlocksOrder = (
  offerId: string,
  bodyBlocksOrder: string[] | undefined,
  offers: IBuyOneGetManyOffersByBlock,
): number | undefined => {
  const withinBlockIndex = findOfferIndex(getOnePlusManyOffers(offers), offerId);
  if (withinBlockIndex === -1) return undefined;

  let order = bodyBlocksOrder;
  if (order === undefined) order = [];
  const onePlusManyBlockPosition = order.indexOf('onePlusMany');
  if (onePlusManyBlockPosition === -1) return withinBlockIndex + 1;

  const precedingOffersCount = countPrecedingOffers(
    order,
    onePlusManyBlockPosition,
    offers,
  );
  return precedingOffersCount + withinBlockIndex + 1;
};

const resolveOnePlusManyCap = (
  maxOffers: StoreBlockMaxOffers | undefined,
): number | 'unlimited' => {
  if (maxOffers === undefined || maxOffers.unlimited) return 'unlimited';
  if (maxOffers.value === undefined) return 0;
  return maxOffers.value;
};

interface IOnePlusManyPoolSlice {
  slice: OfferData[];
  nextCursor: number;
}

const sliceOnePlusManyPoolForBlock = (
  block: TStoreBlock,
  pool: OfferData[],
  cursor: number,
): IOnePlusManyPoolSlice => {
  if (!isOnePlusManyStoreBlock(block)) {
    return { slice: [], nextCursor: cursor };
  }

  const cap = resolveOnePlusManyCap(block.maxOffers);
  if (cap === 'unlimited') {
    return { slice: pool.slice(cursor), nextCursor: pool.length };
  }

  return { slice: pool.slice(cursor, cursor + cap), nextCursor: cursor + cap };
};

interface IOnePlusManyBlockWalk {
  foundPosition?: number;
  offersConsumed: number;
  nextCursor: number;
}

const walkOnePlusManyBlock = (
  block: TStoreBlock,
  offerId: string,
  pool: OfferData[],
  cursor: number,
  positionBefore: number,
): IOnePlusManyBlockWalk => {
  const { slice, nextCursor } = sliceOnePlusManyPoolForBlock(block, pool, cursor);
  const withinInstanceIndex = findOfferIndex(slice, offerId);
  if (withinInstanceIndex !== -1) {
    return {
      foundPosition: positionBefore + withinInstanceIndex + 1,
      offersConsumed: slice.length,
      nextCursor,
    };
  }
  return { offersConsumed: slice.length, nextCursor };
};

const countGenericBlockOffers = (
  block: TStoreBlock,
  offers: IBuyOneGetManyOffersByBlock,
  countedOfferKeys: Set<keyof IBuyOneGetManyOffersByBlock>,
): number => {
  const offersKey = STORE_BLOCK_TYPE_TO_OFFERS_KEY[block.type];
  if (offersKey === undefined || countedOfferKeys.has(offersKey)) return 0;
  countedOfferKeys.add(offersKey);
  return countOffersForKey(offers, offersKey);
};

const computeOfferPositionFromStoreBlocks = (
  offerId: string,
  storeBlocks: TStoreBlock[],
  offers: IBuyOneGetManyOffersByBlock,
): number | undefined => {
  const onePlusManyOffers = getOnePlusManyOffers(offers);
  const couldNotFindOffer = findOfferIndex(onePlusManyOffers, offerId) === -1;
  if (couldNotFindOffer) return undefined;

  let position = 0;
  let poolCursor = 0;
  const countedOfferKeys = new Set<keyof IBuyOneGetManyOffersByBlock>();

  for (const block of storeBlocks) {
    if (!block.isVisible) continue;

    if (block.type === EBlockType.ONE_PLUS_MANY) {
      const walk = walkOnePlusManyBlock(
        block,
        offerId,
        onePlusManyOffers,
        poolCursor,
        position,
      );
      if (walk.foundPosition !== undefined) return walk.foundPosition;
      position += walk.offersConsumed;
      poolCursor = walk.nextCursor;
      continue;
    }

    position += countGenericBlockOffers(block, offers, countedOfferKeys);
  }

  return undefined;
};

export const computeBuyOneGetManyOfferPosition = (
  offerId: string,
  bodyBlocksOrder: string[] | undefined,
  offers: IBuyOneGetManyOffersByBlock,
  storeBlocks?: TStoreBlock[],
): number | undefined => {
  if (storeBlocks !== undefined && storeBlocks.length > 0) {
    return computeOfferPositionFromStoreBlocks(offerId, storeBlocks, offers);
  }

  return computeOfferPositionFromBodyBlocksOrder(
    offerId,
    bodyBlocksOrder,
    offers,
  );
};

interface IBuyOneGetManyEventProduct {
  product_id: string;
  product_name: string;
  product_quantity: number;
}

const mapTileProductsToEventProducts = (
  tile: SequencedProduct | undefined,
): IBuyOneGetManyEventProduct[] => {
  if (tile === undefined) return [];
  return tile.products.map((product) => ({
    product_id: product.publisherProductId,
    product_name: product.name,
    product_quantity: product.quantity,
  }));
};

interface IBuyOneGetManyTilePricing {
  priceInUsdCents: number;
  localPriceAmount: number;
}

const extractTilePricing = (
  tile: SequencedProduct | undefined,
): IBuyOneGetManyTilePricing => {
  const prices = tile?.prices;
  let priceInUsdCents = 0;
  if (prices?.priceInUsdCents !== undefined) {
    priceInUsdCents = prices.priceInUsdCents;
  }
  let localPriceAmount = 0;
  if (prices?.price !== undefined) {
    localPriceAmount = prices.price;
  }
  return { priceInUsdCents, localPriceAmount };
};

const resolveSalePercentage = (offer: OfferData): number | undefined => {
  const sale = offer.productSale?.sale;
  if (sale !== undefined && sale !== null) return sale;
  return offer.dynamicOfferUi?.salePercentage;
};

interface IBuildBuyOneGetManyEventPropsArgs {
  offer: OfferData;
  offers: IBuyOneGetManyOffersByBlock;
  bodyBlocksOrder: string[] | undefined;
  storeBlocks?: TStoreBlock[];
  sharedContext: IBuyOneGetManyEventSharedContext;
}

export const buildBuyOneGetManyEventProps = ({
  offer,
  offers,
  bodyBlocksOrder,
  storeBlocks,
  sharedContext,
}: IBuildBuyOneGetManyEventPropsArgs): Record<string, unknown> => {
  const tile = getCurrentSubRollingOffer(offer);
  const { priceInUsdCents, localPriceAmount } = extractTilePricing(tile);

  return {
    offer_id: offer.offerId,
    offer_name: offer.offerName,
    offer_external_id: offer.publisherOfferId,
    currency: tile?.prices?.currencyCode,
    products: mapTileProductsToEventProducts(tile),
    sale_percentage: resolveSalePercentage(offer),
    type: BUY_ONE_GET_MANY_EVENT_TYPE,
    sub_type: offer.subType === undefined ? null : offer.subType,
    amount_gross_usd: priceInUsdCents / 100,
    amount_gross_local: localPriceAmount,
    total_tiles: offer.productsSequence.length,
    index: offer.indexToCollect,
    is_free: priceInUsdCents === 0,
    offer_position: computeBuyOneGetManyOfferPosition(
      offer.offerId,
      bodyBlocksOrder,
      offers,
      storeBlocks,
    ),
    os: sharedContext.os,
    browser: sharedContext.browser,
    country_code: sharedContext.countryCode,
    url: window.location.origin,
  };
};
